import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import profilePic from '../assets/Davis_2026.png';
import './About.css';

export function About() {
  return (
    <div className="about">
      <Hero
        title="About"
        subtitle="The story behind Notation of Being"
        size="small"
      />

      <section className="about__content">
        <div className="container container--narrow">
          <div className="about__author">
            <div className="about__author-image">
              <img src={profilePic} alt="Spencer R. Davis" className="about__author-photo" />
            </div>
            <div className="about__author-bio">
              <h2>About the Author</h2>
              <p>
                Spencer R. Davis is a software engineer, data scientist, and systems thinker.
                His career has been spent recognizing patterns in complex systems—and designing,
                building, and rewriting the rules by which they behave, technical and human alike.
              </p>
              <p>
                His work spans large-scale data systems, security analytics, machine learning,
                and applied systems design—domains where small abstractions quietly shape outcomes
                at scale. Over time, that practice sharpened a deeper question: not just how
                systems function, but how they come to feel inevitable—how tools become norms,
                how solutions harden into identities, and how inherited structures begin making
                decisions long before anyone realizes choice is involved.
              </p>
              <p>
                <em>Notation of Being</em> emerged from watching the same dynamic repeat across
                domains. Time becomes a moral scoreboard. Productivity becomes identity. Money
                becomes meaning. "Normal" becomes unquestionable. These symbolic compressions are
                not mistakes—they are survival technologies. They help us function, coordinate,
                and move forward. But left unexamined, they quietly narrow what feels possible,
                permissible, or even thinkable.
              </p>
              <p>
                Spencer does not write to dismantle belief or replace one worldview with another.
                His work takes on a harder task: helping readers develop symbolic literacy—the
                ability to recognize when meaning is alive and adaptive, and when it has frozen
                into habit, policy, or self-judgment.
              </p>
              <p>
                He writes as an engineer, not a philosopher—approaching culture the way one
                approaches systems: with respect for why they exist, clarity about their
                trade-offs, and responsibility for what happens when they stop serving the people
                living inside them. His thinking is shaped as much by marriage, parenting,
                leadership, and ordinary life as by philosophy, systems theory, and cognitive science.
              </p>
              <p>
                Spencer lives in Texas with his wife, their three children, and a dog. His life,
                like everyone else's, is still being written.
              </p>
              <p className="about__author-tagline">
                He does not claim to have escaped notation.<br />
                Only to have learned how to notice it—and how to write with it more consciously.
              </p>
              <div className="about__author-links">
                <a
                  href="https://www.amazon.com/author/spencer-r-davis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="about__author-btn"
                >
                  <i className="fab fa-amazon"></i>
                  <span>View on Amazon</span>
                </a>
              </div>
            </div>
          </div>

          <div className="about__section">
            <h2>The Philosophy</h2>
            <p className="about__lead">
              <em>Notation of Being</em> begins with an overlooked observation:<br />
              humans do not experience reality directly. We experience symbolic representations of it.
            </p>
            <p>
              Time, identity, value, morality, productivity, "normal"—none are facts of nature.
              They are human-made compressions that let finite minds navigate an otherwise
              overwhelming world. Not illusions to discard, but tools to understand.
            </p>
            <p>
              The central concern is not awareness for its own sake, but responsibility. When
              symbolic systems go unrecognized, they govern behavior silently. When they become
              visible, responsibility returns—to individuals, families, and communities.
            </p>
            <p>
              Rather than offering escape, certainty, or transcendence, <em>Notation of Being</em> invites
              a different posture: living with symbols consciously, without collapsing meaning or
              outsourcing agency. Meaning survives awareness. Illusion does not.
            </p>
          </div>

          <div className="about__section">
            <h2>The Mission</h2>
            <p>
              This project explores how humans inherit, inhabit, and author the symbolic systems
              that shape their lives. Through the book and related work, the aim is to:
            </p>
            <ul className="about__list">
              <li>
                <i className="fas fa-check"></i>
                <span>Clarify how everyday ontologies quietly shape identity and behavior</span>
              </li>
              <li>
                <i className="fas fa-check"></i>
                <span>Reveal the trade-offs embedded in "normal" life structures</span>
              </li>
              <li>
                <i className="fas fa-check"></i>
                <span>Encourage conscious authorship without moralizing or dogma</span>
              </li>
              <li>
                <i className="fas fa-check"></i>
                <span>Bridge philosophy, systems thinking, and lived experience</span>
              </li>
              <li>
                <i className="fas fa-check"></i>
                <span>Offer language for noticing when meaning has hardened—and when it can evolve</span>
              </li>
            </ul>
            <p className="about__closing">
              This is not a movement, method, or doctrine.<br />
              It is an invitation to look more closely at the symbols already shaping your
              life—and decide which ones you wish to keep writing.
            </p>
          </div>

          <div className="about__section">
            <h2>Get in Touch</h2>
            <p>
              Have questions, thoughts, or want to connect?<br />
              Reach out through any of the social channels or send a message directly.
            </p>
            <div className="about__contact">
              <a href="mailto:contact@notationofbeing.com" className="about__contact-link">
                <i className="fas fa-envelope"></i>
                <span>contact@notationofbeing.com</span>
              </a>
              <Link to="/contact" className="about__contact-link">
                <i className="fas fa-paper-plane"></i>
                <span>Use the contact form</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
